import { useState } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Beer,
  Home as HomeIcon,
  Music,
  Factory,
  Car,
  HeartPulse,
  ArrowUpRight,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const SERVICES = [
  {
    id: "offices",
    num: "01",
    icon: Building2,
    title: "Corporate HQ & Office Towers",
    badge: "High Density",
    span: "md:col-span-2",
    desc: "Nightly desk-to-door programmes for multi-floor workspaces, engineered around your 9-to-5.",
    scope: ["Workstation & hot-desk sanitisation", "Washroom consumables management", "Reception & glass atrium detailing", "Waste, recycling & WEEE handling"],
  },
  {
    id: "pubs",
    num: "02",
    icon: Beer,
    title: "Pubs, Bars & Hospitality",
    badge: "Night Shift Ready",
    span: "",
    desc: "Post-close deep cleans so you open spotless — cellars, lines, kitchens and front of house.",
    scope: ["Cellar & beer-line area hygiene", "Kitchen degrease & extraction", "Front-of-house polish & glass care", "Pre-opening turnaround crews"],
  },
  {
    id: "homes",
    num: "03",
    icon: HomeIcon,
    title: "Luxury Residential Estates",
    badge: "White Glove",
    span: "",
    desc: "Discreet, vetted operatives for premium homes — meticulous finishes, absolute trust.",
    scope: ["Scheduled white-glove cleans", "End-of-tenancy deep resets", "Soft furnishing & upholstery care", "Keyholder & alarm-managed access"],
  },
  {
    id: "concerts",
    num: "04",
    icon: Music,
    title: "Concerts & Arena Venues",
    badge: "Rapid Post-Event",
    span: "md:col-span-2",
    desc: "Rapid-response crews that turn an 8,000-seat arena around before the next load-in.",
    scope: ["Post-event rapid turnaround", "Seating bowl & concourse resets", "Backstage & hospitality suites", "Waste stream segregation at scale"],
  },
  {
    id: "factories",
    num: "05",
    icon: Factory,
    title: "Factories & Logistics Hubs",
    badge: "Heavy Equipment",
    span: "",
    desc: "Industrial degreasing, high-level cleans and floor care built for 24/7 operations.",
    scope: ["Industrial degreasing programmes", "High-level & racking cleans", "Machine-adjacent floor care", "Shift-pattern aligned scheduling"],
  },
  {
    id: "carwashes",
    num: "06",
    icon: Car,
    title: "Automotive & Car Washes",
    badge: "Water Recirculation",
    span: "",
    desc: "Bay detailing, forecourt care and water-system hygiene for high-throughput sites.",
    scope: ["Wash bay deep detailing", "Forecourt & canopy care", "Water recirculation hygiene", "Valeting bay turnover support"],
  },
  {
    id: "carehomes",
    num: "07",
    icon: HeartPulse,
    title: "Care Homes & Healthcare",
    badge: "Hospital Grade ISO",
    span: "md:col-span-3",
    desc: "Infection-control-led cleaning for vulnerable environments — audited, documented, compassionate.",
    scope: ["CQC-aligned infection control", "Electrostatic disinfection cycles", "Resident-safe low-odour formulas", "Full audit trail & compliance logs"],
  },
];

export const Services = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <p className="eyebrow mb-4">Seven Sectors. One Standard.</p>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ice-50 max-w-2xl leading-snug">
            Wherever hygiene is mission-critical,{" "}
            <span className="text-gold-500">Gold Link is already there.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => (
            <motion.button
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              onClick={() => setActive(s)}
              className={`group relative text-left bg-navy-800/50 border border-white/5 rounded-xl p-7 overflow-hidden transition-all duration-300 hover:border-gold-500/40 hover:bg-navy-800 ${s.span}`}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/0 group-hover:bg-gold-500/10 blur-2xl transition-all duration-500 rounded-full" />
              <div className="flex items-start justify-between mb-8">
                <span className="font-mono text-xs text-gold-500/80 tracking-widest">
                  {s.num}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 border border-white/10 rounded-full px-3 py-1">
                  {s.badge}
                </span>
              </div>
              <s.icon className="w-8 h-8 text-gold-400 mb-5 transition-transform duration-300 group-hover:scale-110" />
              <h3 className="font-heading font-semibold text-xl sm:text-2xl text-ice-50 mb-2.5">
                {s.title}
              </h3>
              <p className="text-sm leading-relaxed text-ice-200/60 mb-6 max-w-md">
                {s.desc}
              </p>
              <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-500 group-hover:gap-3 transition-all duration-300">
                View Scope <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent
          data-testid="service-detail-modal"
          className="bg-navy-800 border-white/10 text-ice-50 max-w-lg"
        >
          {active && (
            <>
              <DialogHeader>
                <p className="font-mono text-xs text-gold-500 tracking-widest mb-2">
                  SECTOR {active.num} — {active.badge}
                </p>
                <DialogTitle className="font-heading text-2xl text-ice-50">
                  {active.title}
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-ice-200/70 leading-relaxed mb-5">
                {active.desc}
              </p>
              <ul className="space-y-3">
                {active.scope.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-ice-200/80">
                    <span className="w-1.5 h-1.5 rotate-45 bg-gold-500 mt-1.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
